(() => {
  // Share provider downloads across section reloads; failed requests can be retried.
  const scripts = new Map();
  function loadScript(source) {
    const src = source.src;
    if (scripts.has(src)) return scripts.get(src);
    const promise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      for (const attr of source.attributes) script.setAttribute(attr.name, attr.value);
      script.async = true;
      const finish = (error) => {
        clearTimeout(timeout);
        script.onload = script.onerror = null;
        if (error) { script.remove(); scripts.delete(src); reject(error); }
        else resolve();
      };
      const timeout = setTimeout(() => finish(new Error('Provider timed out')), 20000);
      script.onload = () => finish();
      script.onerror = () => finish(new Error('Provider failed'));
      document.head.append(script);
    });
    scripts.set(src, promise);
    return promise;
  }

  if (!customElements.get('cro-deferred-video')) {
    customElements.define('cro-deferred-video', class extends HTMLElement {
      connectedCallback() {
        this.controller = new AbortController();
        this.querySelector('button')?.addEventListener('click', () => this.play(), {signal: this.controller.signal});
      }
      disconnectedCallback() { this.controller?.abort(); this.pause(); }
      play() {
        let frame = this.querySelector('iframe');
        if (!frame) {
          const template = this.querySelector('template');
          this.posterTemplate = template.cloneNode(true);
          frame = template.content.querySelector('iframe').cloneNode(true);
          const url = new URL(frame.src);
          url.searchParams.set('autoplay', '1');
          frame.src = url.toString();
          frame.loading = 'eager';
          template.replaceWith(frame);
        }
        this.querySelector('[data-video-poster]').hidden = true;
        frame.focus();
      }
      pause() {
        // Removing the frame also cancels autoplay while a slow provider is still loading.
        const frame = this.querySelector('iframe');
        if (frame && this.posterTemplate) frame.replaceWith(this.posterTemplate.cloneNode(true));
        const poster = this.querySelector('[data-video-poster]');
        if (poster) poster.hidden = false;
      }
    });
  }

  if (!customElements.get('cro-deferred-chat')) {
    customElements.define('cro-deferred-chat', class extends HTMLElement {
      connectedCallback() {
        this.controller = new AbortController();
        this.button = this.querySelector('button');
        this.fallback = this.querySelector('a');
        this.button?.addEventListener('click', () => this.open(), {signal: this.controller.signal});
        if (this.dataset.onClick === 'false') this.open(false);
      }
      disconnectedCallback() { this.controller?.abort(); }
      async open(open = true) {
        if (this.pending) return;
        this.pending = true;
        this.button.disabled = true;
        this.button.setAttribute('aria-busy', 'true');
        try {
          if (typeof window.zE !== 'function') {
            const source = document.createElement('script');
            source.id = 'ze-snippet';
            source.src = 'https://static.zdassets.com/ekr/snippet.js?key=' + encodeURIComponent(this.dataset.widgetKey);
            await loadScript(source);
          }
          if (!this.isConnected) return;
          if (typeof window.zE !== 'function') throw new Error('Chat unavailable');
          const api = this.dataset.api || 'messenger';
          window.zE(api, 'show');
          if (open) window.zE(api, 'open');
          this.button.hidden = true;
          if (this.fallback) this.fallback.hidden = true;
        } catch (error) {
          if (this.fallback) this.fallback.hidden = false;
          this.button.textContent = this.dataset.retryLabel || this.button.textContent;
        } finally {
          this.pending = false;
          this.button.disabled = false;
          this.button.removeAttribute('aria-busy');
        }
      }
    });
  }

  if (!customElements.get('cro-deferred-widget')) {
    customElements.define('cro-deferred-widget', class extends HTMLElement {
      connectedCallback() {
        if (this.loaded) return;
        if (this.pending) { this.resume = true; return; }
        this.controller = new AbortController();
        this.querySelector('[data-retry]')?.addEventListener('click', () => this.load(), {signal: this.controller.signal});
        this.addEventListener('focusin', () => this.load(), {signal: this.controller.signal});
        if (this.dataset.defer !== 'true' || window.Shopify?.designMode || !('IntersectionObserver' in window)) {
          this.load();
        } else {
          this.observer = new IntersectionObserver(entries => {
            if (entries.some(entry => entry.isIntersecting)) this.load();
          }, {rootMargin: '500px 0px'});
          this.observer.observe(this);
        }
      }
      disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); this.cancelReady?.(); }
      waitForReady() {
        const selector = this.dataset.readySelector;
        if (!selector || this.querySelector(selector)) return Promise.resolve();
        return new Promise((resolve, reject) => {
          const finish = error => {
            observer.disconnect(); clearTimeout(timeout); this.cancelReady = null;
            error ? reject(error) : resolve();
          };
          const observer = new MutationObserver(() => { if (this.querySelector(selector)) finish(); });
          const timeout = setTimeout(() => finish(new Error('Widget did not initialize')), 20000);
          this.cancelReady = () => finish(new Error('Section removed'));
          observer.observe(this, {childList: true, subtree: true});
        });
      }
      async load() {
        if (this.pending || this.loaded || !this.isConnected) return;
        this.pending = true;
        this.observer?.disconnect();
        this.setAttribute('aria-busy', 'true');
        const retry = this.querySelector('[data-retry]');
        if (retry) retry.hidden = true;
        try {
          if (!this.sources) {
            const fragment = this.querySelector('template').content.cloneNode(true);
            this.sources = [...fragment.querySelectorAll('script')].filter(script => !script.type || /javascript|module/.test(script.type));
            this.sources.forEach(script => script.remove());
            this.renderedNodes = [...fragment.childNodes];
            this.querySelector('template').before(fragment);
          }
          for (const source of this.sources) {
            if (!this.isConnected) return;
            if (source.executed) continue;
            if (source.src) await loadScript(source);
            else {
              const script = document.createElement('script');
              for (const attr of source.attributes) script.setAttribute(attr.name, attr.value);
              script.textContent = source.textContent;
              let failure;
              const onError = event => { failure = event.error || new Error(event.message); };
              window.addEventListener('error', onError);
              this.append(script);
              window.removeEventListener('error', onError);
              if (failure) { script.remove(); throw failure; }
            }
            source.executed = true;
          }
          await this.waitForReady();
          this.loaded = true;
          this.dataset.loaded = 'true';
        } catch (error) {
          // Re-run initialization on retry; provider downloads remain shared.
          this.renderedNodes?.forEach(node => node.remove());
          this.querySelector('[data-widget-target]')?.replaceChildren();
          this.sources = null;
          if (retry) retry.hidden = false;
        } finally {
          this.pending = false;
          this.removeAttribute('aria-busy');
          if (this.resume && this.isConnected) { this.resume = false; this.connectedCallback(); }
        }
      }
    });
  }
})();
